import { useMutation, gql } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { useState } from 'react';

import { DeleteDialog } from 'src/components/DeleteDialog/DeleteDialog';
import {
  EditJobNotes,
  type Mode,
} from 'src/components/JobNotes/EditJobNotes/EditJobNotes';
import { JOB_TYPE } from 'src/const/app';
import translations from 'src/containers/JobNotes/Edit.vocab';
import { analyticsClient } from 'src/modules/analytics/analytics';
import type {
  SetAppliedJobNotesMutationVariables,
  SetAppliedJobNotesMutation,
  AppliedJob,
  RemoveAppliedJobNotesMutation,
  RemoveAppliedJobNotesMutationVariables,
  SetSavedJobNotesMutationVariables,
  SetSavedJobNotesMutation,
  SavedJob,
  RemoveSavedJobNotesMutation,
  RemoveSavedJobNotesMutationVariables,
} from 'src/types/graphql';
import { getJobStatus } from 'src/utils';

import { REMOVE_APPLIED_JOB_NOTES } from './remove-applied-job-notes.graphql';
import { REMOVE_SAVED_JOB_NOTES } from './remove-saved-job-notes.graphql';
import { SET_APPLIED_JOB_NOTES } from './set-applied-job-notes.graphql';
import { SET_SAVED_JOB_NOTES } from './set-saved-job-notes.graphql';

interface Props {
  index: number;
  job: AppliedJob | SavedJob;
  notes?: string;
  setView: () => void;
  getIsDirty: () => void;
  type: typeof JOB_TYPE.applied | typeof JOB_TYPE.saved;
}

export const Edit = ({
  job,
  index,
  setView,
  notes,
  getIsDirty,
  type,
}: Props) => {
  const { t } = useTranslations(translations);
  const jobId = job?.id;
  const { locale } = useMelwaysInfo();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [
    setAppliedJobNotes,
    { loading: loadingSaveAppliedJob, error: setMutationErrorAppliedJob },
  ] = useMutation<
    SetAppliedJobNotesMutation,
    SetAppliedJobNotesMutationVariables
  >(SET_APPLIED_JOB_NOTES, {
    errorPolicy: 'all',
    refetchQueries: ['GetAppliedJobs'],
    update(cache, _, { variables }) {
      cache.writeFragment({
        id: cache.identify(job),
        fragment: gql`
          fragment AppliedJobNotes on AppliedJob {
            notes
          }
        `,
        data: { notes: variables?.input.notes || '' },
      });
    },
  });

  const [
    setSavedJobNotes,
    { loading: loadingSaveSavedJob, error: setMutationErrorSavedJob },
  ] = useMutation<SetSavedJobNotesMutation, SetSavedJobNotesMutationVariables>(
    SET_SAVED_JOB_NOTES,
    {
      errorPolicy: 'all',
      refetchQueries: ['GetSavedJobs'],
      update(cache, _, { variables }) {
        cache.writeFragment({
          id: cache.identify(job),
          fragment: gql`
            fragment SavedJobNotes on SavedJob {
              notes
            }
          `,
          data: { notes: variables?.input.notes || '' },
        });
      },
    },
  );

  const onSave = (updatedNotes: string, mode: Mode) => {
    analyticsClient.trackLink({
      eventName: 'notes_update_pressed',
      updateType: mode,
      jobId,
      jobListingDate: job.job.createdAt.dateTimeUtc,
      jobLocation: job.job.location.label,
      jobSalary: job.job.salary?.label,
      jobStatus: getJobStatus(job.isActive),
      jobTitle: job.job.title,
      jobIsLinkOut: job.isExternal,
      advertiserName: job.job.advertiser.name,
    });

    const setJobNotes =
      type === JOB_TYPE.applied ? setAppliedJobNotes : setSavedJobNotes;

    setJobNotes({
      variables: {
        input: {
          id: jobId,
          notes: updatedNotes,
        },
        locale,
      },
    }).then(() => {
      setView();
    });
  };

  const [
    removeAppliedJobNotes,
    { loading: loadingDeleteAppliedJob, error: removeMutationErrorAppliedJob },
  ] = useMutation<
    RemoveAppliedJobNotesMutation,
    RemoveAppliedJobNotesMutationVariables
  >(REMOVE_APPLIED_JOB_NOTES, {
    errorPolicy: 'all',
    refetchQueries: ['GetAppliedJobs'],
    update(cache) {
      cache.writeFragment({
        id: cache.identify(job),
        fragment: gql`
          fragment AppliedJobNotes on AppliedJob {
            notes
          }
        `,
        data: { notes: '' },
      });
    },
  });

  const [
    removeSavedJobNotes,
    { loading: loadingDeleteSavedJob, error: removeMutationErrorSavedJob },
  ] = useMutation<
    RemoveSavedJobNotesMutation,
    RemoveSavedJobNotesMutationVariables
  >(REMOVE_SAVED_JOB_NOTES, {
    errorPolicy: 'all',
    refetchQueries: ['GetSavedJobs'],
    update(cache) {
      cache.writeFragment({
        id: cache.identify(job),
        fragment: gql`
          fragment SavedJobNotes on SavedJob {
            notes
          }
        `,
        data: { notes: '' },
      });
    },
  });

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const processDelete = () => {
    setShowDeleteDialog(false);
    analyticsClient.trackLink({
      eventName: 'notes_update_pressed',
      updateType: 'delete',
      jobId,
      jobListingDate: job.job.createdAt.dateTimeUtc,
      jobLocation: job.job.location.label,
      jobSalary: job.job.salary?.label,
      jobStatus: getJobStatus(job.isActive),
      jobTitle: job.job.title,
      jobIsLinkOut: job.isExternal,
      advertiserName: job.job.advertiser.name,
    });

    const removeJobNotes =
      type === JOB_TYPE.applied ? removeAppliedJobNotes : removeSavedJobNotes;

    removeJobNotes({
      variables: {
        jobId,
        locale,
      },
    }).then(() => {
      setView();
    });
  };

  const error =
    setMutationErrorAppliedJob ||
    setMutationErrorSavedJob ||
    removeMutationErrorAppliedJob ||
    removeMutationErrorSavedJob
      ? t(`Sorry, we couldn’t save your changes. Can we try again?`)
      : undefined;

  return (
    <>
      <DeleteDialog
        index={index}
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        processDelete={processDelete}
        title={t('Delete notes')}
        message={t(`Are you sure you want to delete this?`)}
      />
      <EditJobNotes
        index={index}
        pending={
          loadingSaveAppliedJob ||
          loadingSaveSavedJob ||
          loadingDeleteAppliedJob ||
          loadingDeleteSavedJob
        }
        value={notes}
        error={error}
        onCancel={setView}
        onSave={onSave}
        onDelete={onDelete}
        getIsDirty={getIsDirty}
      />
    </>
  );
};
